import classNames from 'classnames';
import React, { FC, memo, useCallback, useLayoutEffect, useState } from 'react';
import { Image, Badge } from 'antd';
import { Popup } from 'shared/ui/Popup';
import { Trans } from 'react-i18next';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { GeolocationCoords } from 'app/types/common';
import { getWorkingScheduleString, WarehouseForBooking } from 'entities/Warehouse';
import { ReactComponent as CameraIcon } from 'shared/assets/icons/CameraIcon.svg';
import { ReactComponent as ClockIcon } from 'shared/assets/icons/ClockIcon.svg';
import { ReactComponent as EmailIcon } from 'shared/assets/icons/EmailIcon.svg';
import { ReactComponent as PaymentCardIcon } from 'shared/assets/icons/PaymentCardIcon.svg';
import { ReactComponent as PhoneIcon } from 'shared/assets/icons/PhoneIcon.svg';
import { ReactComponent as TrolleyIcon } from 'shared/assets/icons/TrolleyIcon.svg';
import { ReactComponent as SmartphoneIcon } from 'shared/assets/icons/SmartphoneIcon.svg';
import WarehouseDefaultImage from 'shared/assets/images/WarehouseDefaultImage.png';
import { Button } from 'shared/ui/Button';
import { CopyTooltip } from 'shared/ui/CopyTooltip';
import { ReadMore } from 'shared/ui/ReadMore';
import { Tag } from 'shared/ui/Tag';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { getBoxDiscountedPriceAmount, getBoxPriceAmountWithVat, useGetMinBoxPriceAmount } from 'entities/Box';
import { warehouseMapActions } from '../../model/slice/warehouseMapSlice';
import { SizeGroupsTags } from 'entities/SizeCode';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { getLoggedUserData } from 'entities/User';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { WarnTag } from 'shared/ui/WarnTag';

interface ExpandedWarehouseCardProps {
  warehouse: WarehouseForBooking;
  distance?: Nullable<number>;
  isOutlined: boolean;
  onSelectCard: (coords: GeolocationCoords) => void;
  onViewBoxes: () => void;
  clipCard: () => void;
}

export const ExpandedWarehouseCard: FC<ExpandedWarehouseCardProps> = memo((props) => {
  const {
    warehouse: {
      warehouseId,
      name,
      imageUrl,
      address,
      email,
      zip,
      phone,
      description,
      workingSchedule,
      sizeCodes,
      minPrice,
      longitude,
      latitude,
      availableBoxesAmount,
      launchDate,
      isComingSoon,
      warehouseDiscounts,
      videoGuideUrl,
      hasOffices,
      warningInfo,
    },
    isOutlined,
    distance,
    onViewBoxes,
    onSelectCard,
    clipCard,
  } = props;

  const { t } = useAppTranslation('booking');

  const dispatch = useAppDispatch();

  const { scrollToElement, scrollRef } = useScrollToElement('start', 200);

  useLayoutEffect(() => {
    isOutlined && scrollToElement();
  }, [isOutlined, scrollToElement]);

  const currencySymbol = useGetCurrencySymbol();

  const globalSettings = useAppSelector(getGlobalSettings);

  const loggedUser = useAppSelector(getLoggedUserData);

  const fullAddress = `${address} ${zip || ''}`;
  const warehouseImage = imageUrl || WarehouseDefaultImage;

  const workingScheduleString = getWorkingScheduleString(workingSchedule);

  const minBoxPrice = useGetMinBoxPriceAmount(props.warehouse);

  const discountedPrice = getBoxDiscountedPriceAmount({ warehouseDiscounts, boxPrice: minPrice });

  const discountedPriceWithVat = getBoxPriceAmountWithVat(discountedPrice, props.warehouse, loggedUser?.accountType);

  const discountedBoxPrice = roundNumber(globalSettings?.showPriceWithVat ? discountedPriceWithVat : discountedPrice);

  const [isPreviewVisible, setIsPreviewVisible] = useState(false);

  const selectCard = (): void => {
    onSelectCard({ lat: latitude, lng: longitude });
  };

  const handleViewBoxes = useCallback((): void => {
    dispatch(warehouseMapActions.setSelectedCard(warehouseId));
    onViewBoxes();
  }, [dispatch, onViewBoxes, warehouseId]);

  const onCloseCard = useCallback((): void => {
    dispatch(warehouseMapActions.setSelectedCard(null));
    clipCard();
  }, [clipCard, dispatch]);

  return (
    <div
      ref={scrollRef}
      className={classNames('rounded-lg bg-white ease-linear duration-200 cursor-pointer', {
        'shadow-borderLike shadow-accent': isOutlined,
      })}
      onClick={selectCard}
    >
      {isComingSoon && launchDate && (
        <div className="text-center py-2 bg-accent text-white rounded">
          {t('Openning')} {getClientDateFormat(launchDate)}!
        </div>
      )}

      {hasOffices && <Badge.Ribbon className="bg-accent" text={t('Has offices')} />}

      <div className="p-4">
        <div className="w-full flex flex-col justify-between mt-4 mb-2 desktop:mt-auto">
          <div className="font-semibold text-md desktop:text-xl mobile:text-xl">{name}</div>
          <div className="w-full flex flex-row justify-between desktop:mt-auto">
            <CopyTooltip className="cursor-pointer text-primaryLight text-sm font-normal">{fullAddress}</CopyTooltip>
            {distance && <div className="font-semibold text-sm relative">{t('{{distance}} km', { distance })}</div>}
          </div>
        </div>

        <div className="my-4">
          <Image
            height={190}
            width="100%"
            className="rounded-lg object-cover"
            src={warehouseImage}
            preview={{
              visible: isPreviewVisible,
              scaleStep: 3,
              src: warehouseImage,
              onVisibleChange: (value) => {
                setIsPreviewVisible(value);
              },
            }}
          />
        </div>

        <Button
          containerClassName="items-end"
          theme="clear"
          onClick={() => {
            setIsPreviewVisible(true);
          }}
        >
          {t('Expand image')}
        </Button>

        {Boolean(minBoxPrice) && (
          <div className="shrink-0 text-2xl font-semibold mb-3">
            <Trans
              t={t}
              i18nKey={`${
                discountedBoxPrice ? 'From <0>{{discountedPrice}} {{currencySymbol}}/month</0>' : ''
              } <1>From {{minPrice}} {{currencySymbol}}/month</1>`}
              components={[
                <span key="0" className="text-accent text-xl font-semibold" />,
                <div key="1" className={discountedBoxPrice ? 'line-through text-error' : 'text-primary text-xl font-semibold'} />,
              ]}
              values={{ minPrice: minBoxPrice, currencySymbol, discountedPrice: discountedBoxPrice }}
            />
          </div>
        )}
        <div className="space-y-2.5">
          <div className="flex items-center space-x-2.5">
            <EmailIcon className="shrink-0 [&>path]:stroke-accent" />
            <CopyTooltip className="cursor-pointer text-primaryLight text-sm font-normal">{email}</CopyTooltip>
          </div>
          <div className="flex items-center space-x-2.5">
            <PhoneIcon className="shrink-0 [&>path]:stroke-accent" />
            <CopyTooltip className="cursor-pointer text-primaryLight text-sm font-normal">{phone}</CopyTooltip>
          </div>
          <div className="flex items-center text-primaryLight text-sm font-normal">
            <ClockIcon className="mr-2 shrink-0 [&>path]:stroke-accent [&>circle]:stroke-accent" />
            {workingScheduleString}
          </div>
          {warningInfo && (
            <div className="flex items-center text-primaryLight text-sm font-normal">
              <WarnTag textSize="small" className="font-semibold px-0" iconClassName="w-[23px] h-[23px]">
                <span className="text-sm font-semibold">{getLocalizedString(warningInfo)}</span>
              </WarnTag>
            </div>
          )}
        </div>
        <div className="text-success my-4">{t('{{availableBoxesAmount}} boxes free', { availableBoxesAmount })}</div>
        <div className="flex flex-row items-center">
          <SizeGroupsTags sizeCodes={sizeCodes} />
          <div>
            {hasOffices && (
              <Popup
                title={t('Office rent')}
                description={t('On this location you can rent office')}
                color="var(--accent)"
                textColor="var(--white)"
              >
                <div
                  className={classNames(
                    'flex justify-center items-center text-white text-sm font-normal bg-accent rounded-lg w-20 h-8 cursor-pointer mb-[5px] ml-5',
                  )}
                >
                  {t('Offices')}
                </div>
              </Popup>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 my-4">
          <Tag icon={<CameraIcon className="[&>path]:fill-accent" />}>{t('Security cameras monitoring')}</Tag>
          {!workingSchedule.length && (
            <Tag icon={<ClockIcon className="[&>*]:stroke-accent [&>*]:stroke-1" />}>{t('24 / 7 direct access')}</Tag>
          )}
          <Tag icon={<TrolleyIcon className="[&>path]:fill-accent" />}>{t('Free use of trolleys')}</Tag>
          <Tag icon={<SmartphoneIcon className="[&>path]:stroke-accent" />}>{t('Opened by app')}</Tag>
          <Tag icon={<PaymentCardIcon className="stroke-accent" />}>{t('Online payments')}</Tag>
        </div>

        {videoGuideUrl && (
          <div className="my-5">
            <a href={videoGuideUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-accent">
              &#128165;{t('How to use this warehouse?')}&#128165;
            </a>
          </div>
        )}
        <ReadMore html={getLocalizedString(description)} rows={3} scrollToElement={scrollToElement} />
        <div className="flex space-x-2.5">
          <Button containerClassName="flex-1" theme="secondary" onClick={onCloseCard}>
            {t('Close')}
          </Button>
          <Button containerClassName="flex-1" onClick={handleViewBoxes}>
            {t('View boxes')}
          </Button>
        </div>
      </div>
    </div>
  );
});
